// Sidebar navigation component.
// Collapsible, with expandable sections for Framework and Settings.
// Active route highlighting via TanStack Router.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRouterState, useNavigate, useLocation } from '@tanstack/react-router'
import { ChevronDown, ChevronRight, PanelLeftClose, PanelLeft, CircleHelp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { getNavItems, type NavItem, type NavChild } from '@/lib/navigation'
import { useCurrentUserOrNull } from '@/lib/hooks/use-current-user'
import { resolveHelpTopic, openGuide } from '@/lib/helpTopics'
import { cn } from '@/lib/utils/cn'

interface SidebarProps {
  slug: string
  collapsed: boolean
  onToggleCollapse: () => void
}

export function Sidebar({ slug, collapsed, onToggleCollapse }: SidebarProps) {
  const { t } = useTranslation('layout')
  const me = useCurrentUserOrNull()
  const isAdmin = me?.is_tenant_admin ?? false
  const navItems = getNavItems().filter((item) => !item.adminOnly || isAdmin)

  return (
    <aside
      className={cn(
        'flex h-full flex-col border-r bg-sidebar text-sidebar-foreground transition-all duration-200',
        collapsed ? 'w-16' : 'w-60',
      )}
    >
      {/* Top bar: help + collapse toggle */}
      <div className="flex h-10 items-center border-b px-2">
        <HelpButton slug={slug} collapsed={collapsed} />
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleCollapse}
          className="ml-auto h-8 w-8 text-sidebar-foreground"
          aria-label={collapsed ? t('sidebar.expandSidebar') : t('sidebar.collapseSidebar')}
        >
          {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </Button>
      </div>

      {/* Navigation items */}
      <nav className="flex-1 overflow-y-auto py-2">
        <TooltipProvider delayDuration={0}>
          <ul className="space-y-1 px-2">
            {navItems.map((item) => (
              <SidebarNavItem key={item.path} item={item} slug={slug} collapsed={collapsed} isAdmin={isAdmin} />
            ))}
          </ul>
        </TooltipProvider>
      </nav>

      {/* Footer */}
      <div className="border-t p-2">
        {!collapsed && (
          <p className="truncate px-2 py-1 text-xs text-muted-foreground">
            {t('sidebar.appVersion')}
          </p>
        )}
      </div>
    </aside>
  )
}

interface SidebarNavItemProps {
  item: NavItem
  slug: string
  collapsed: boolean
  isAdmin: boolean
}

function SidebarNavItem({ item, slug, collapsed, isAdmin }: SidebarNavItemProps) {
  const { t } = useTranslation('layout')
  const routerState = useRouterState()
  const navigate = useNavigate()
  const currentPath = routerState.location.pathname
  const fullPath = `/t/${slug}${item.path}`
  const isActive = currentPath === fullPath || currentPath.startsWith(`${fullPath}/`)
  const label = t(item.labelKey)

  // Filter children by admin status
  const visibleChildren = item.children?.filter((c) => !c.adminOnly || isAdmin)

  // For items with children, track expanded state — default to expanded if active
  const [expanded, setExpanded] = useState(isActive)

  const hasChildren = visibleChildren && visibleChildren.length > 0
  const Icon = item.icon

  function handleNavigate(path: string): void {
    navigate({ to: path })
  }

  // Collapsed mode: show icon with tooltip
  if (collapsed) {
    const targetPath =
      hasChildren && visibleChildren?.[0] ? `/t/${slug}${visibleChildren[0].path}` : fullPath

    return (
      <li>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => handleNavigate(targetPath)}
              className={cn(
                'flex h-9 w-full items-center justify-center rounded-md transition-colors',
                isActive
                  ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                  : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
              )}
            >
              <Icon className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">{label}</TooltipContent>
        </Tooltip>
      </li>
    )
  }

  // Expanded mode with children
  if (hasChildren) {
    return (
      <li>
        <button
          onClick={() => setExpanded(!expanded)}
          className={cn(
            'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
            isActive
              ? 'bg-sidebar-accent text-sidebar-accent-foreground'
              : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
          )}
        >
          <Icon className="h-4 w-4 shrink-0" />
          <span className="flex-1 text-left">{label}</span>
          {expanded ? (
            <ChevronDown className="h-3 w-3 shrink-0" />
          ) : (
            <ChevronRight className="h-3 w-3 shrink-0" />
          )}
        </button>
        {expanded && (
          <ul className="ml-4 mt-1 space-y-1 border-l border-sidebar-border pl-3">
            {visibleChildren?.map((child) => (
              <SidebarChildItem key={child.path} child={child} slug={slug} isAdmin={isAdmin} />
            ))}
          </ul>
        )}
      </li>
    )
  }

  // Simple item without children
  return (
    <li>
      <button
        onClick={() => handleNavigate(fullPath)}
        className={cn(
          'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
          isActive
            ? 'bg-sidebar-accent text-sidebar-accent-foreground'
            : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        )}
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span>{label}</span>
      </button>
    </li>
  )
}

interface SidebarChildItemProps {
  child: NavChild
  slug: string
  isAdmin: boolean
}

function SidebarChildItem({ child, slug, isAdmin }: SidebarChildItemProps) {
  const { t } = useTranslation('layout')
  const routerState = useRouterState()
  const navigate = useNavigate()
  const currentPath = routerState.location.pathname
  const hasChildren = child.children && child.children.length > 0

  // For sub-groups, check if any child route is active
  const isChildActive = hasChildren
    ? child.children!.some((c) => currentPath === `/t/${slug}${c.path}`)
    : false

  const fullPath = `/t/${slug}${child.path}`
  const isActive = !hasChildren && currentPath === fullPath

  const [expanded, setExpanded] = useState(isChildActive)

  // Sub-group with its own children — collapsible
  if (hasChildren) {
    return (
      <li>
        <button
          onClick={() => setExpanded(!expanded)}
          className={cn(
            'flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-sm transition-colors',
            isChildActive
              ? 'font-medium text-sidebar-accent-foreground'
              : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
          )}
        >
          <span className="flex-1">{t(child.labelKey)}</span>
          {expanded ? (
            <ChevronDown className="h-3 w-3 shrink-0" />
          ) : (
            <ChevronRight className="h-3 w-3 shrink-0" />
          )}
        </button>
        {expanded && (
          <ul className="ml-3 mt-1 space-y-1 border-l border-sidebar-border pl-3">
            {child.children!.filter((c) => !c.adminOnly || isAdmin).map((subChild) => (
              <SidebarChildItem key={subChild.path} child={subChild} slug={slug} isAdmin={isAdmin} />
            ))}
          </ul>
        )}
      </li>
    )
  }

  // Leaf item
  return (
    <li>
      <button
        onClick={() => navigate({ to: fullPath })}
        className={cn(
          'block w-full rounded-md px-3 py-1.5 text-left text-sm transition-colors',
          isActive
            ? 'bg-sidebar-accent font-medium text-sidebar-accent-foreground'
            : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        )}
      >
        {t(child.labelKey)}
      </button>
    </li>
  )
}

// --- Help button (opens guide in new window) ---

interface HelpButtonProps {
  slug: string
  collapsed: boolean
}

function HelpButton({ slug, collapsed }: HelpButtonProps) {
  const { t } = useTranslation('layout')
  const location = useLocation()

  function handleClick() {
    openGuide(resolveHelpTopic(location.pathname), slug)
  }

  if (collapsed) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClick}
              className="h-8 w-8 text-sidebar-foreground"
            >
              <CircleHelp className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">{t('sidebar.help')}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleClick}
      className="h-8 gap-2 px-2 text-sidebar-foreground"
    >
      <CircleHelp className="h-4 w-4" />
      <span className="text-sm">{t('sidebar.help')}</span>
    </Button>
  )
}
